import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDemo2Ctx } from '../Demo2Context';
import { TREES, ZONES, STATUS_LABEL, STATUS_COLOR, treeIconRi, treeDataBadge, type MoistStatus } from '../demo2Data';

const STATS: { key: MoistStatus | 'all'; label: string }[] = [
    { key: 'all', label: '전체' }, { key: 'good', label: '양호' },
    { key: 'caution', label: '주의' }, { key: 'warn', label: '위험' }, { key: 'danger', label: '심각' },
];

export default function Demo2TreeList() {
    const navigate = useNavigate();
    const { toast } = useDemo2Ctx();
    const [statFilter, setStatFilter] = useState<MoistStatus | 'all'>('all');
    const [zoneFilter, setZoneFilter] = useState('all');
    const [q, setQ] = useState('');

    const countFor = (k: MoistStatus | 'all') => (k === 'all' ? TREES.length : TREES.filter(t => t.status === k).length);

    const list = useMemo(() => {
        const query = q.trim().toLowerCase();
        return TREES.filter(t => {
            if (statFilter !== 'all' && t.status !== statFilter) return false;
            if (zoneFilter !== 'all' && t.zone !== zoneFilter) return false;
            if (query && !(t.species.toLowerCase().includes(query) || t.zoneName.toLowerCase().includes(query) || t.id.toLowerCase().includes(query) || t.code.toLowerCase().includes(query))) return false;
            return true;
        });
    }, [statFilter, zoneFilter, q]);

    return (
        <div className="list-page">
            <div className="list-bar">
                <div className="chips">
                    {STATS.map(o => (
                        <div key={o.key} className={`chip${statFilter === o.key ? ' active' : ''}`} onClick={() => setStatFilter(o.key)}>
                            {o.label}<span style={{ marginLeft: 4, fontSize: 10, fontWeight: 700, opacity: 0.7 }}>{countFor(o.key)}</span>
                        </div>
                    ))}
                </div>
                <input type="text" className="form-input" placeholder="수종, 구역명, 수목ID 검색..." style={{ width: 230, height: 34, fontSize: 12.5 }} value={q} onChange={e => setQ(e.target.value)} />
                <select className="form-select" style={{ width: 200, height: 34, fontSize: 12 }} value={zoneFilter} onChange={e => setZoneFilter(e.target.value)}>
                    <option value="all">전체 관리구역</option>
                    {ZONES.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
                </select>
                <div style={{ flex: 1 }} />
                <span className="text-xs text-muted">{list.length}주 표시 중 (전체 {TREES.length}주)</span>
                <button className="btn btn-secondary btn-sm" onClick={() => toast('Excel 내보내기', 'success')}><i className="ri-download-2-line" /> Excel</button>
            </div>

            <div className="tbl-wrap">
                <table className="data-tbl">
                    <thead>
                        <tr>
                            <th style={{ width: 34 }}>#</th><th>상태</th><th>수목ID</th><th>수종</th>
                            <th>위치 (관리구역)</th><th>수고</th><th>흉고둘레</th>
                            <th style={{ minWidth: 130 }}>수분율</th><th style={{ minWidth: 128 }}>데이터 방식</th><th>식재일</th><th>마지막 관개일</th><th>액션</th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.length === 0 && (
                            <tr><td colSpan={12} style={{ textAlign: 'center', padding: '36px 0', color: 'var(--text3)' }}>조건에 해당하는 수목이 없습니다</td></tr>
                        )}
                        {list.map((t, i) => {
                            const days = Math.round((new Date('2026-07-20').getTime() - new Date(t.lastDate).getTime()) / 86400000);
                            const badge = treeDataBadge(t);
                            return (
                                <tr key={t.id}>
                                    <td className="text-muted">{i + 1}</td>
                                    <td><span className={`badge badge-${t.status}`}><span className={`badge-dot dot-${t.status}`} />{STATUS_LABEL[t.status]}</span></td>
                                    <td className="bold text-mono">{t.id}<div className="text-xs text-muted">{t.code}</div></td>
                                    <td><span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 700, color: '#166534' }}><i className={treeIconRi(t.species)} /> {t.species}</span></td>
                                    <td>{t.zoneName}<div className="text-xs text-muted">{t.district}</div></td>
                                    <td className="text-mono">{t.height}m</td>
                                    <td className="text-mono">{t.girth}cm</td>
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <div className="prog"><div className="prog-fill" style={{ width: `${t.moist}%`, background: STATUS_COLOR[t.status] }} /></div>
                                            <span className="text-mono" style={{ color: STATUS_COLOR[t.status], fontWeight: 700 }}>{t.moist}%</span>
                                        </div>
                                    </td>
                                    <td><span className={`tree-data-badge ${badge.tone}`}><i className={badge.icoRi} /> {badge.text}</span></td>
                                    <td className="text-mono text-xs">{t.planted}</td>
                                    <td className="text-mono" style={{ color: days >= 7 ? 'var(--danger)' : days >= 4 ? 'var(--warn)' : 'var(--text3)', fontWeight: days >= 7 ? 700 : 400 }}>{t.lastDate}</td>
                                    <td>
                                        <div style={{ display: 'flex', gap: 6 }}>
                                            <button className="btn btn-info btn-sm" onClick={() => navigate(`/demo2/web/tree-detail/${t.id}`)}><i className="ri-eye-line" /> 상세보기</button>
                                            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/demo2/web/map', { state: { zoneId: t.zone } })}><i className="ri-map-pin-line" /> 지도보기</button>
                                            <button className="btn btn-primary btn-sm" onClick={() => navigate('/demo2/web/work-management')}><i className="ri-drop-line" /> 관개등록</button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="pagination">
                <button className="pg-btn" onClick={() => toast('이전 페이지', 'success')}>◀</button>
                <button className="pg-btn active">1</button>
                <button className="pg-btn" onClick={() => toast('2 페이지', 'success')}>2</button>
                <button className="pg-btn" onClick={() => toast('3 페이지', 'success')}>3</button>
                <button className="pg-btn" onClick={() => toast('다음 페이지', 'success')}>▶</button>
            </div>
        </div>
    );
}
